import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  Loader2,
  Download,
  FileText,
  Receipt,
  ArrowLeftRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  transaction_buyer: { label: 'Purchase', color: 'bg-emerald-100 text-emerald-800' },
  transaction_seller_fee: { label: 'Sale fee', color: 'bg-amber-100 text-amber-800' },
  subscription: { label: 'WANTD+', color: 'bg-indigo-100 text-indigo-800' },
  boost: { label: 'Boost', color: 'bg-orange-100 text-orange-800' },
  plus_listing: { label: 'Plus', color: 'bg-sky-100 text-sky-800' },
};

const STATUS_LABELS = {
  paid: { label: 'Paid', color: 'text-emerald-700 bg-emerald-50' },
  refunded: { label: 'Refunded', color: 'text-amber-700 bg-amber-50' },
  failed: { label: 'Failed', color: 'text-red-700 bg-red-50' },
  pending: { label: 'Pending', color: 'text-gray-600 bg-gray-50' },
  awaiting_payment: { label: 'Awaiting', color: 'text-blue-700 bg-blue-50' },
  completed: { label: 'Completed', color: 'text-emerald-700 bg-emerald-50' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50' },
};

const INVOICE_TABS = [
  { id: 'all', label: 'All', types: null },
  { id: 'subscription', label: 'Subscriptions', types: 'subscription' },
  { id: 'boost', label: 'Boosts', types: 'boost,plus_listing' },
  { id: 'purchases', label: 'Purchases', types: 'transaction_buyer' },
  { id: 'sales', label: 'Sale Fees', types: 'transaction_seller_fee' },
];

const TX_STATUS_TABS = [
  { id: 'all', label: 'All', value: null },
  { id: 'pending', label: 'Pending', value: 'pending' },
  { id: 'awaiting_payment', label: 'Awaiting Payment', value: 'awaiting_payment' },
  { id: 'paid', label: 'Paid', value: 'paid' },
  { id: 'completed', label: 'Completed', value: 'completed' },
];

function fmtCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Invoice tab
// ---------------------------------------------------------------------------
function InvoiceView({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchRecords = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        let url = `${API_URL}/api/billing/admin/records?limit=20`;
        const tab = INVOICE_TABS.find((t) => t.id === activeTab);
        if (tab?.types) url += `&type=${tab.types}`;
        if (dateFrom) url += `&date_from=${dateFrom}`;
        if (dateTo) url += `&date_to=${dateTo}`;
        if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
        if (searchQuery) {
          if (searchQuery.toUpperCase().startsWith('INV-')) {
            url += `&invoice_number=${encodeURIComponent(searchQuery)}`;
          } else {
            url += `&user_id=${encodeURIComponent(searchQuery)}`;
          }
        }
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.data || {};
        setRecords((prev) => append ? [...prev, ...(data.records || [])] : data.records || []);
        setNextCursor(data.next_cursor || null);
      } catch (err) {
        toast.error(err.response?.status === 403 ? 'Admin access required' : 'Failed to load billing records');
      } finally { setLoading(false); setLoadingMore(false); }
    },
    [token, activeTab, dateFrom, dateTo, searchQuery]
  );

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleDownload = async (record) => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/admin/records/${record.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `WANTD-${record.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download invoice'); }
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); setNextCursor(null); fetchRecords(); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="User ID or INV-YYYY-NNNNN..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9956C]/30 focus:border-[#C9956C]" />
          </div>
          <Button type="submit" className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white">Search</Button>
        </form>
        <div className="flex flex-wrap gap-2">
          {INVOICE_TABS.map((tab) => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setNextCursor(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                activeTab === tab.id ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}>{tab.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">Date:</span>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setNextCursor(null); }}
            className="px-2 py-1 border border-gray-200 rounded text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setNextCursor(null); }}
            className="px-2 py-1 border border-gray-200 rounded text-sm" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setNextCursor(null); }}
              className="text-[#C9956C] text-xs hover:underline">Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No invoices found</p>
          <p className="text-sm mt-1">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Invoice</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Description</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">GST</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const type = TYPE_LABELS[r.type] || { label: r.type, color: 'bg-gray-100 text-gray-800' };
                  const status = STATUS_LABELS[r.status] || { label: r.status, color: 'text-gray-700 bg-gray-50' };
                  return (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => setSelectedRecord(r)}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.invoice_number}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${type.color}`}>{type.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="truncate max-w-[120px]" title={r.user_id}>{r.username || r.user_id?.slice(0, 8) + '...'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700"><div className="truncate max-w-[200px]">{r.description}</div></td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmtCents(r.amount_cents)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{r.gst_cents > 0 ? fmtCents(r.gst_cents) : '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.issued_at || r.created_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(r); }} disabled={r.status !== 'paid'}
                          className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Download PDF"><Download className="w-4 h-4 text-gray-600" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => fetchRecords({ cursor: nextCursor, append: true })} disabled={loadingMore}>
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Load more
              </Button>
            </div>
          )}
        </>
      )}

      {selectedRecord && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRecord(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">{selectedRecord.invoice_number}</h2>
              <button onClick={() => setSelectedRecord(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-gray-500">Type</div><div className="font-medium">{(TYPE_LABELS[selectedRecord.type] || { label: selectedRecord.type }).label}</div></div>
              <div><div className="text-gray-500">Status</div><div className="font-medium">{selectedRecord.status}</div></div>
              <div><div className="text-gray-500">Amount</div><div className="font-medium">{fmtCents(selectedRecord.amount_cents)}</div></div>
              <div><div className="text-gray-500">GST</div><div className="font-medium">{selectedRecord.gst_cents > 0 ? fmtCents(selectedRecord.gst_cents) : 'N/A'}</div></div>
              <div><div className="text-gray-500">User</div><div className="font-medium">{selectedRecord.username || '-'}</div></div>
              <div><div className="text-gray-500">User ID</div><div className="font-mono text-xs break-all">{selectedRecord.user_id}</div></div>
              <div className="col-span-2"><div className="text-gray-500">Description</div><div className="font-medium">{selectedRecord.description}</div></div>
              {selectedRecord.stripe_object_id && (
                <div className="col-span-2"><div className="text-gray-500">Stripe Object ID</div><div className="font-mono text-xs break-all">{selectedRecord.stripe_object_id}</div></div>
              )}
              {selectedRecord.ref_id && (
                <div className="col-span-2"><div className="text-gray-500">Reference ID</div><div className="font-mono text-xs break-all">{selectedRecord.ref_id}</div></div>
              )}
              <div><div className="text-gray-500">Issued</div><div className="text-xs">{fmtDate(selectedRecord.issued_at)}</div></div>
              <div><div className="text-gray-500">Created</div><div className="text-xs">{fmtDate(selectedRecord.created_at)}</div></div>
              {selectedRecord.metadata && Object.keys(selectedRecord.metadata).length > 0 && (
                <div className="col-span-2">
                  <div className="text-gray-500 mb-1">Metadata</div>
                  <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">{JSON.stringify(selectedRecord.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => handleDownload(selectedRecord)} disabled={selectedRecord.status !== 'paid'}>
                <Download className="w-4 h-4 mr-1" />Download PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSelectedRecord(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Transactions tab
// ---------------------------------------------------------------------------
function TransactionView({ token }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);

  const fetchTransactions = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        let url = `${API_URL}/api/billing/admin/transactions?limit=20`;
        const tab = TX_STATUS_TABS.find((t) => t.id === activeTab);
        if (tab?.value) url += `&status=${tab.value}`;
        if (dateFrom) url += `&date_from=${dateFrom}`;
        if (dateTo) url += `&date_to=${dateTo}`;
        if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
        if (searchQuery) {
          if (searchQuery.length > 20) {
            url += `&transaction_id=${encodeURIComponent(searchQuery)}`;
          } else {
            url += `&user_id=${encodeURIComponent(searchQuery)}`;
          }
        }
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.data || {};
        setTransactions((prev) => append ? [...prev, ...(data.transactions || [])] : data.transactions || []);
        setNextCursor(data.next_cursor || null);
      } catch (err) {
        toast.error(err.response?.status === 403 ? 'Admin access required' : 'Failed to load transactions');
      } finally { setLoading(false); setLoadingMore(false); }
    },
    [token, activeTab, dateFrom, dateTo, searchQuery]
  );

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); setNextCursor(null); fetchTransactions(); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="User ID or transaction ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9956C]/30 focus:border-[#C9956C]" />
          </div>
          <Button type="submit" className="bg-[#1E3A5F] hover:bg-[#152d4a] text-white">Search</Button>
        </form>
        <div className="flex flex-wrap gap-2">
          {TX_STATUS_TABS.map((tab) => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setNextCursor(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                activeTab === tab.id ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}>{tab.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">Date:</span>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setNextCursor(null); }}
            className="px-2 py-1 border border-gray-200 rounded text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setNextCursor(null); }}
            className="px-2 py-1 border border-gray-200 rounded text-sm" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setNextCursor(null); }}
              className="text-[#C9956C] text-xs hover:underline">Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" /></div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No transactions found</p>
          <p className="text-sm mt-1">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">TX ID</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Item</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Buyer</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Seller</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Item Value</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Fee</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Payment</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const status = STATUS_LABELS[tx.status] || { label: tx.status, color: 'text-gray-700 bg-gray-50' };
                  return (
                    <tr key={tx.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => setSelectedTx(tx)}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        <div className="truncate max-w-[80px]" title={tx.id}>{tx.id.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="truncate max-w-[160px]">{tx.item_title || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="truncate max-w-[100px]" title={tx.buyer_id}>{tx.buyer_username || tx.buyer_id?.slice(0, 8) + '...'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="truncate max-w-[100px]" title={tx.seller_id}>{tx.seller_username || tx.seller_id?.slice(0, 8) + '...'}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmtCents(tx.item_value_cents)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{fmtCents(tx.fee_cents)}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{tx.payment_method || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(tx.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => fetchTransactions({ cursor: nextCursor, append: true })} disabled={loadingMore}>
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Load more
              </Button>
            </div>
          )}
        </>
      )}

      {selectedTx && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedTx(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Transaction Details</h2>
              <button onClick={() => setSelectedTx(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2"><div className="text-gray-500">Transaction ID</div><div className="font-mono text-xs break-all">{selectedTx.id}</div></div>
              <div><div className="text-gray-500">Status</div>
                <div className="font-medium">{(STATUS_LABELS[selectedTx.status] || { label: selectedTx.status }).label}</div>
              </div>
              <div><div className="text-gray-500">Payment Method</div><div className="font-medium">{selectedTx.payment_method || 'Not set'}</div></div>
              <div className="col-span-2"><div className="text-gray-500">Item</div><div className="font-medium">{selectedTx.item_title || '-'}</div></div>
              <div><div className="text-gray-500">Item ID</div><div className="font-mono text-xs break-all">{selectedTx.item_id}</div></div>
              <div><div className="text-gray-500">Item Value</div><div className="font-medium">{fmtCents(selectedTx.item_value_cents)}</div></div>
              <div><div className="text-gray-500">Buyer</div><div className="font-medium">{selectedTx.buyer_username || '-'}</div></div>
              <div><div className="text-gray-500">Seller</div><div className="font-medium">{selectedTx.seller_username || '-'}</div></div>
              <div><div className="text-gray-500">Buyer ID</div><div className="font-mono text-xs break-all">{selectedTx.buyer_id}</div></div>
              <div><div className="text-gray-500">Seller ID</div><div className="font-mono text-xs break-all">{selectedTx.seller_id}</div></div>
              <div><div className="text-gray-500">Fee</div><div className="font-medium">{fmtCents(selectedTx.fee_cents)}</div></div>
              <div><div className="text-gray-500">Buyer Charged</div><div className="font-medium">{fmtCents(selectedTx.buyer_charged_cents)}</div></div>
              <div><div className="text-gray-500">Seller Credited</div><div className="font-medium">{fmtCents(selectedTx.seller_credited_cents)}</div></div>
              {selectedTx.stripe_payment_intent_id && (
                <div className="col-span-2"><div className="text-gray-500">Stripe PaymentIntent</div><div className="font-mono text-xs break-all">{selectedTx.stripe_payment_intent_id}</div></div>
              )}
              <div><div className="text-gray-500">Created</div><div className="text-xs">{fmtDate(selectedTx.created_at)}</div></div>
              <div><div className="text-gray-500">Paid</div><div className="text-xs">{selectedTx.paid_at ? fmtDate(selectedTx.paid_at) : '-'}</div></div>
              <div><div className="text-gray-500">Completed</div><div className="text-xs">{selectedTx.completed_at ? fmtDate(selectedTx.completed_at) : '-'}</div></div>
            </div>
            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setSelectedTx(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page shell — tab toggle between Invoices and Transactions
// ---------------------------------------------------------------------------
const VIEWS = [
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
];

export default function AdminBillingPage() {
  const { token } = useAuth();
  const [view, setView] = useState('invoices');

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
              <p className="text-sm text-gray-500">Invoices, receipts, and C2C transactions</p>
            </div>
          </div>

          <div className="flex bg-gray-100 rounded-lg p-1">
            {VIEWS.map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.id} onClick={() => setView(v.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {view === 'invoices' ? <InvoiceView token={token} /> : <TransactionView token={token} />}
      </div>
    </AdminLayout>
  );
}
